import { useState, useMemo } from 'react';

/**
 * Custom hook for pagination logic
 * @param {Array} items - The full list of items to paginate
 * @param {Object} options - Configuration options
 * @param {number} options.initialPageSize - Number of items per page
 * @param {boolean} options.isMobile - Whether to use mobile "load more" logic
 */
export function usePagination(items = [], { initialPageSize = 10, isMobile = false } = {}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [visibleCount, setVisibleCount] = useState(initialPageSize);

    const totalItems = items.length;
    const totalPages = Math.ceil(totalItems / initialPageSize);

    // For desktop: standard slicing
    const paginatedItems = useMemo(() => {
        if (isMobile) {
            return items.slice(0, visibleCount);
        }
        const start = (currentPage - 1) * initialPageSize;
        return items.slice(start, start + initialPageSize);
    }, [items, currentPage, initialPageSize, isMobile, visibleCount]);

    const hasMore = isMobile ? visibleCount < totalItems : currentPage < totalPages;

    const loadMore = () => {
        if (isMobile && hasMore) {
            setVisibleCount(prev => prev + initialPageSize);
        }
    };

    const goToPage = (page) => {
        if (!isMobile) {
            const pageNumber = Math.max(1, Math.min(page, totalPages));
            setCurrentPage(pageNumber);
        }
    };

    const nextPage = () => goToPage(currentPage + 1);
    const prevPage = () => goToPage(currentPage - 1);

    return {
        items: paginatedItems,
        currentPage,
        totalPages,
        totalItems,
        hasMore,
        loadMore,
        goToPage,
        nextPage,
        prevPage,
        pageSize: initialPageSize
    };
}
